/**
 * Choosing how slides look.
 *
 * Every swatch on this screen is a real slide, painted by `drawSlide` — the
 * same function that writes the exported PNGs and fills the deck overview. A
 * mocked-up preview in CSS would have been less code and would have started
 * lying the first time the renderer changed. It also means the swatches change
 * *shape* the moment the aspect ratio does, which is the whole point of
 * offering the choice.
 *
 * Two axes, in the order they matter:
 *
 * 1. **Aspect ratio** — the shape of the screen in the room. Get this wrong and
 *    no palette saves the deck: a 16:9 slide on a 4:3 projector is a band
 *    across the middle of the screen.
 * 2. **Theme** — colour and type face.
 *
 * Margins, sizes and spacing are fixed across every theme *and* both ratios,
 * because those are what keep a slide readable from the back row — see the note
 * at the top of `lib/slide-themes.ts`.
 */

import { useEffect, useRef } from "react";
import { AlertTriangle, Check } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SettingsSection } from "@/components/settings/SettingsSection";
import { useTheme } from "@/contexts/ThemeContext";
import { drawSlideScaled } from "@/lib/slide-render";
import { parseSlides } from "@/lib/slides";
import {
  MIN_SLIDE_CONTRAST,
  SLIDE_ASPECTS,
  SLIDE_THEMES,
  contrastRatio,
  isHexColour,
  resolveSlideTheme,
  type CustomSlideTheme,
  type SlideAspect,
  type SlideFace,
  type SlideStyle,
  type SlideTheme,
} from "@/lib/slide-themes";
import { cn } from "@/lib/utils";

/**
 * The slide every swatch shows.
 *
 * A title, a line of body copy and a quotation, so one card exercises both
 * faces and all three inks. Parsed rather than hand-built so it goes through
 * exactly the path a real slide does.
 */
const SAMPLE = parseSlides(
  "```slide\n# The Prodigal Son\n\n- Still a long way off\n\n> His father saw him.\n```",
).slides[0];

/** Small enough for a grid of cards, big enough to judge on a retina screen. */
const SWATCH_RATIO = 0.22;

function SlideSwatch({ style, className }: { style: SlideStyle; className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current && SAMPLE) {
      drawSlideScaled(canvasRef.current, SAMPLE, SWATCH_RATIO, { total: 3, ...style });
    }
  }, [style]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={cn("block h-auto w-full", className)}
      style={{ aspectRatio: `${style.aspect.width} / ${style.aspect.height}` }}
    />
  );
}

/**
 * One selectable card: a real slide, its name, and what it is for.
 *
 * Shared by both pickers, so the aspect ratio is chosen the same way the theme
 * is — by looking at the thing itself rather than reading a label.
 */
function SlideCard({
  style,
  name,
  description,
  isActive,
  onSelect,
  frame,
}: {
  style: SlideStyle;
  name: string;
  description: string;
  isActive: boolean;
  onSelect: () => void;
  /**
   * Letterbox the swatch into a fixed-height box.
   *
   * Only the aspect picker uses it: two cards of different heights side by side
   * would read as two sizes of card rather than two shapes of slide, which is
   * exactly the comparison being offered.
   */
  frame?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isActive}
      className={cn(
        "group overflow-hidden rounded-xl border text-left transition-all",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        isActive
          ? "border-primary ring-1 ring-primary/30"
          : "border-border hover:border-primary/50",
      )}
    >
      <div className={cn("relative", frame && "flex h-40 items-center justify-center bg-muted p-3")}>
        <SlideSwatch style={style} className={frame ? "h-full w-auto max-w-full" : undefined} />
        {isActive && (
          <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Check className="h-3 w-3" />
          </span>
        )}
      </div>
      <div className="border-t border-border px-3 py-2">
        <div className="text-sm text-foreground">{name}</div>
        <div className="text-xs leading-snug text-muted-foreground">{description}</div>
      </div>
    </button>
  );
}

function ColourField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const id = `slide-colour-${label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div className="flex items-center justify-between gap-3">
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="color"
          value={isHexColour(value) ? value : "#000000"}
          onChange={(event) => onChange(event.target.value)}
          className="h-8 w-10 cursor-pointer rounded border border-border bg-transparent p-0.5"
        />
        <input
          type="text"
          value={value}
          spellCheck={false}
          // Typed hex is applied only once it is a complete colour, so the
          // preview never blanks out halfway through "#1B1D24".
          onChange={(event) => {
            const next = event.target.value.trim();
            if (isHexColour(next)) onChange(next);
          }}
          onBlur={(event) => {
            if (!isHexColour(event.target.value.trim())) event.target.value = value;
          }}
          defaultValue={value}
          key={value}
          aria-label={`${label} hex value`}
          className="w-24 rounded border border-border bg-background px-2 py-1 font-mono text-xs text-foreground"
        />
      </div>
    </div>
  );
}

function FaceField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: SlideFace;
  onChange: (next: SlideFace) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm">{label}</span>
      <div className="flex overflow-hidden rounded-lg border border-border">
        {(["sans", "serif"] as const).map((face) => (
          <button
            key={face}
            type="button"
            onClick={() => onChange(face)}
            aria-pressed={value === face}
            className={cn(
              "px-3 py-1 text-xs capitalize transition-colors",
              value === face
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted",
            )}
            style={{ fontFamily: face === "serif" ? "Georgia, serif" : undefined }}
          >
            {face}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Warn when a custom palette will not survive the room.
 *
 * A warning, never a refusal — the palette belongs to whoever set it, and this
 * app cannot see the room. But ink at 2:1 against its page is invisible from
 * the back row, and a Sunday morning is a terrible time to find that out.
 */
function ContrastWarning({ theme }: { theme: SlideTheme }) {
  const ratio = contrastRatio(theme.ink, theme.background);
  if (ratio >= MIN_SLIDE_CONTRAST) return null;

  return (
    <p className="flex items-start gap-2 rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground" />
      <span>
        Text and page are only {ratio.toFixed(1)}:1 apart. A projector in a lit room washes
        out far more contrast than a screen at arm&rsquo;s length — aim for{" "}
        {MIN_SLIDE_CONTRAST}:1 or more.
      </span>
    </p>
  );
}

export function SlideThemeSelector() {
  const {
    slideThemeId,
    customSlideTheme,
    slideAspect,
    slideAspectId,
    setSlideThemeId,
    setCustomSlideTheme,
    setSlideAspectId,
  } = useTheme();

  const custom = resolveSlideTheme("custom", customSlideTheme);
  const patch = (changes: Partial<CustomSlideTheme>) =>
    setCustomSlideTheme({ ...customSlideTheme, ...changes });

  /** Every theme swatch is drawn in the ratio actually chosen. */
  const withAspect = (theme: SlideTheme): SlideStyle => ({ theme, aspect: slideAspect });
  /** Every ratio swatch is drawn in the theme actually chosen. */
  const activeTheme = resolveSlideTheme(slideThemeId, customSlideTheme);
  const withTheme = (aspect: SlideAspect): SlideStyle => ({ theme: activeTheme, aspect });

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Screen shape"
        description="The proportions of the screen you project onto. Every preview, and all four exports, follow it."
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {SLIDE_ASPECTS.map((aspect) => (
            <SlideCard
              key={aspect.id}
              style={withTheme(aspect)}
              name={aspect.name}
              description={aspect.description}
              isActive={slideAspectId === aspect.id}
              onSelect={() => setSlideAspectId(aspect.id)}
              frame
            />
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Margins and type sizes are the same either way — 4:3 gives a slide more room
          below, not smaller words. If you are not sure, ask whoever runs the desk, or
          leave it at 16:9.
        </p>
      </SettingsSection>

      <SettingsSection
        title="Slide theme"
        description="How exported slides look on a screen. Independent of the app theme — you can write in the dark and still project onto white."
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {SLIDE_THEMES.map((theme) => (
            <SlideCard
              key={theme.id}
              style={withAspect(theme)}
              name={theme.name}
              description={theme.description}
              isActive={slideThemeId === theme.id}
              onSelect={() => setSlideThemeId(theme.id)}
            />
          ))}
          <SlideCard
            style={withAspect(custom)}
            name="Custom"
            description="Your own colours and faces."
            isActive={slideThemeId === "custom"}
            onSelect={() => setSlideThemeId("custom")}
          />
        </div>
      </SettingsSection>

      {slideThemeId === "custom" && (
        <SettingsSection
          title="Custom palette"
          description="Changes show in the swatch above and in every slide you export."
        >
          <div className="space-y-4 rounded-xl border border-border p-4">
            <ColourField
              label="Page"
              value={customSlideTheme.background}
              onChange={(background) => patch({ background })}
            />
            <ColourField
              label="Text"
              value={customSlideTheme.ink}
              onChange={(ink) => patch({ ink })}
            />
            <ColourField
              label="Secondary"
              value={customSlideTheme.soft}
              onChange={(soft) => patch({ soft })}
            />
            <ColourField
              label="Accent"
              value={customSlideTheme.accent}
              onChange={(accent) => patch({ accent })}
            />

            <ContrastWarning theme={custom} />

            <div className="h-px bg-border" />

            <FaceField
              label="Titles and body"
              value={customSlideTheme.titleFace}
              onChange={(titleFace) => patch({ titleFace })}
            />
            <FaceField
              label="Quotations"
              value={customSlideTheme.quoteFace}
              onChange={(quoteFace) => patch({ quoteFace })}
            />

            <div className="h-px bg-border" />

            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="slide-ornament" className="cursor-pointer font-normal">
                <span className="block">Accent rule and slide numbers</span>
                <span className="block text-xs text-muted-foreground">
                  Off leaves nothing on the page but the words.
                </span>
              </Label>
              <Switch
                id="slide-ornament"
                checked={customSlideTheme.ornament}
                onCheckedChange={(ornament) => patch({ ornament })}
              />
            </div>
          </div>
        </SettingsSection>
      )}
    </div>
  );
}
